import type { PatternMeta } from './types';

/**
 * Client-side search over the pattern registry.
 *
 * Deliberately simple: lowercase substring matching across every field a visitor might
 * plausibly type. People search this kind of reference in three different vocabularies, 
 * the pattern name ("focus trap"), the symptom ("focus escapes the modal"), or the
 * criterion ("2.4.7", "Focus Visible"), so all three are indexed. Fuzzy matching would
 * add a dependency and produce confident wrong answers; substring matching produces no
 * results when there are none, which is honest and easy to explain in the empty state.
 */
function haystack(pattern: PatternMeta): string {
  return [
    pattern.title,
    pattern.problem,
    pattern.brokenBehaviour,
    pattern.section508,
    ...pattern.keywords,
    ...pattern.criteria.flatMap((c) => [c.number, c.name, `level ${c.level}`, c.why]),
  ]
    .join('   ')
    .toLowerCase();
}

/**
 * Filter patterns by a free-text query.
 *
 * Multiple whitespace-separated terms are ANDed, so "modal escape" narrows rather than
 * widens. An empty or whitespace-only query returns everything, unfiltered.
 */
export function searchPatterns(
  patterns: readonly PatternMeta[],
  query: string,
): readonly PatternMeta[] {
  const terms = query.trim().toLowerCase().split(/\s+/).filter((t) => t !== '');
  if (terms.length === 0) return patterns;

  return patterns.filter((pattern) => {
    const text = haystack(pattern);
    return terms.every((term) => text.includes(term));
  });
}
